import { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ReactSelect from 'react-select';
import { DatePicker, message, Space, ConfigProvider } from 'antd';
import az_AZ from 'antd/lib/locale/az_AZ';
import en_US from 'antd/lib/locale/en_US';
import ru_RU from 'antd/lib/locale/ru_RU';
import 'moment/locale/az';
import 'moment/locale/ru';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from 'config/query-client';
import moment from 'moment-timezone';

import useTranslation from 'customHooks/translate';
import {
  ACCOUNT_INFO,
  COST,
  CREDITORS,
  CURRENCIES,
  INVOICE_DRAFTS,
  INVOICE_JOBS,
  INVOICE_LANGUAGE,
  JOB_NUMBERS,
  LANGUAGES,
  MANAGERS,
  PARTNERS,
} from 'events/types';
import { getAccountData } from 'services/accounts';
import styled from 'styled-components';
import { toastError } from 'services/helpers/getError';
import {
  getCreditors,
  getCreditorDataById,
  getDebitorDataById,
} from 'services/partners';
import { getManagers } from 'services/managers';
import { getJobs } from 'services/jobs';
import { getCurrencies, getDocTypes, getLanguages } from 'services/collections';
import { getInvoiceLang } from 'services/others';
import {
  createCost,
  deleteDraftCost,
  getCost,
  updateCostDraft,
} from 'services/invoices';
import { NewCostCustomError, NewCostDataType, NewInvoiceOption } from 'types';
import { characterGroups } from 'consts';

import AddNewPartnerForm from './Dialogs/AddNewPartnerForm';
import AddNewJobNumberForm from './Dialogs/AddNewJobNumberForm';
import AddNewManagerForm from './Dialogs/AddNewManagerForm';

import './styles/Invoice.css';

export const NEW_COST_DEFAULT_VALUES: NewCostDataType = {
  language: null,
  contractNumber: '',
  contractDate: null,
  dueDate: '',
  invoiceDate: moment()
    .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
    .format('YYYY-MM-DD'),
  managerId: null,
  invoiceNumber: '',
  currencyId: null,
  exchangeRate: 0,
  documentTypeId: null,
  partnerId: null,
  invoiceJobId: null,
  termOfDue: 0,
};

const NewCost = () => {
  const { state } = useLocation();
  const translation = useTranslation();
  const navigate = useNavigate();
  const { draftId } = useParams();

  const [openManager, setOpenManager] = useState(false);
  const handleOpenManager = () => setOpenManager(true);
  const handleCloseManager = () => setOpenManager(false);
  const [openCustomerName, setOpenCustomerName] = useState(false);
  const handleOpenCustomerName = () => setOpenCustomerName(true);
  const handleCloseCustomerName = () => setOpenCustomerName(false);
  const [openJobNumber, setOpenJobNumber] = useState(false);

  const [locale, setLocale] = useState(en_US);
  const [invoiceLang, setInvoiceLang] = useState<string>('');
  const [selectedPartner, setSelectedPartner] = useState<number>();
  const [docTypes, setDocTypes] = useState<any[]>([]);
  const [initialValues, setInitialValues] = useState<NewCostDataType>(
    NEW_COST_DEFAULT_VALUES
  );
  const [initialized, setInitialized] = useState(false);
  const [formData, setFormData] = useState<NewCostDataType>(
    NEW_COST_DEFAULT_VALUES
  );
  const [exchangeRateReadonly, setExchangeRateReadonly] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>(
    {}
  );
  const [lastAddedPartner, setLastAddedPartner] =
    useState<NewInvoiceOption | null>(null);

  const isEditMode = Boolean(state?.record);
  const currentLanguageRef = useRef<string>('');

  const changeLocale = (lang: string) => {
    if (lang === 'az') {
      setLocale(az_AZ);
      moment.locale('az');
    } else if (lang === 'ru') {
      setLocale(ru_RU);
      moment.locale('ru');
    } else {
      setLocale(en_US);
      moment.locale('en');
    }
  };

  useEffect(() => {
    const handleLanguageChange = () => {
      const currentLang = localStorage.getItem('language') || 'en';
      changeLocale(currentLang);
    };

    handleLanguageChange();
    window.addEventListener('storage', handleLanguageChange);
    const interval = setInterval(handleLanguageChange, 500);

    return () => {
      window.removeEventListener('storage', handleLanguageChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (formData.language?.value) {
      const langValue = formData.language.value as string;
      let langCode = 'en';

      if (langValue.toLowerCase().includes('az') || langValue === 'az') {
        langCode = 'az';
      } else if (langValue.toLowerCase().includes('ru') || langValue === 'ru') {
        langCode = 'ru';
      }

      changeLocale(langCode);
    }
  }, [formData.language]);

  const { data: partners, refetch } = useQuery({
    queryKey: [CREDITORS, draftId, state?.base],
    queryFn: () => getCreditors(),
    select: (response) => {
      return response.data.data.map((d) => ({ label: d.name, value: d.id }));
    },
  });

  const { data: managers } = useQuery({
    queryKey: [MANAGERS],
    queryFn: getManagers,
    select: (response) => {
      return response.data.data.map((d) => ({ label: d.name, value: d.id }));
    },
  });

  const accountInfoQuery = useQuery({
    queryKey: [ACCOUNT_INFO],
    queryFn: getAccountData,
    select: (response) => response.data.data,
  });

  const { data: jobNumbers, refetch: refetchJobNumbers } = useQuery({
    queryKey: [JOB_NUMBERS, state?.record?.invoiceId],
    queryFn: () => getJobs(state?.record?.invoiceId),
    select: (response) => {
      return response.data.data.map((d) => ({ label: d.name, value: d.id }));
    },
  });

  const { data: currencies } = useQuery({
    queryKey: [CURRENCIES],
    queryFn: getCurrencies,
    select: (response) => {
      return response.data.data.map((d) => ({
        label: d.name,
        value: d.currencyId,
      }));
    },
  });

  const { data: languages } = useQuery({
    queryKey: [LANGUAGES],
    queryFn: getLanguages,
    select: (response) => {
      return response.data.data.map((d) => ({ label: d.name, value: d.id }));
    },
  });

  const { data: fetchedInvoiceLang, isSuccess: isLangFetched } = useQuery(
    [INVOICE_LANGUAGE],
    getInvoiceLang,
    {
      enabled: !state?.record && !invoiceLang,
    }
  );

  const { data: existingCostData } = useQuery(
    [COST, state?.record?.invoiceId, state?.base],
    () => getCost(state?.record.invoiceId),
    {
      enabled: !!state?.record && !initialized,
      select: (response) => response.data.data,
    }
  );

  const formattedOptions = useMemo(
    () => ({
      partners: partners || [],
      managers: managers || [],
      jobNumbers: jobNumbers || [],
      currencies: currencies || [],
      languages: languages || [],
    }),
    [partners, managers, jobNumbers, currencies, languages]
  );

  useEffect(() => {
    if (
      state?.record &&
      existingCostData &&
      !initialized &&
      formattedOptions.languages.length &&
      formattedOptions.partners.length
    ) {
      const invoiceData = existingCostData;

      setFormData({
        ...invoiceData,
        language:
          formattedOptions.languages.find(
            (l) => l.value === invoiceData.language
          ) || null,
        partnerId:
          formattedOptions.partners.find(
            (p) => p.value === invoiceData.partnerId
          ) || null,
        managerId:
          formattedOptions.managers.find(
            (m) => m.value === invoiceData.managerId
          ) || null,
        currencyId:
          formattedOptions.currencies.find(
            (c) => c.value === invoiceData.currencyId
          ) || null,
        invoiceJobId:
          formattedOptions.jobNumbers.find(
            (j) => j.value === invoiceData.invoiceJobId
          ) || null,
        documentTypeId: { value: invoiceData.documentTypeId, label: '' },
      });

      setInitialized(true);
    }
  }, [state?.record, existingCostData, initialized, formattedOptions]);

  const getPartnerData = (partnerId: number, from: string) => {
    return from === 'debitor'
      ? getDebitorDataById(partnerId)
      : getCreditorDataById(partnerId);
  };

  useEffect(() => {
    if (
      isLangFetched &&
      fetchedInvoiceLang?.data &&
      formattedOptions.languages.length
    ) {
      const lang = formattedOptions.languages.find(
        (l) => l.value === fetchedInvoiceLang.data
      );
      if (lang) {
        setInvoiceLang(fetchedInvoiceLang.data);
        setInitialValues((prevValues) => ({
          ...prevValues,
          language: lang,
        }));
      }
    }
  }, [isLangFetched, fetchedInvoiceLang, formattedOptions.languages]);

  useEffect(() => {
    const language = formData.language?.value as string;

    if (
      !formData.language &&
      invoiceLang &&
      formattedOptions.languages.length
    ) {
      const selectedLang = formattedOptions.languages.find(
        (lang) => lang.value === invoiceLang
      );
      if (selectedLang) {
        setFormData((prev) => ({
          ...prev,
          language: selectedLang,
        }));
      }
    }

    if (language && language !== currentLanguageRef.current) {
      currentLanguageRef.current = language;
      setInvoiceLang(language);

      getDocTypes(language).then((response) => {
        const documentTypeOptions = response.data.data.map((d) => ({
          label: d.name,
          value: d.id,
        }));

        setDocTypes(documentTypeOptions);

        setFormData((prev) => ({
          ...prev,
          documentTypeId: documentTypeOptions.find(
            (type) => type.value === formData.documentTypeId?.value
          ),
        }));
      });
    }
  }, [formData.language, invoiceLang, formattedOptions.languages]);

  useEffect(() => {
    const partnerId = formData.partnerId?.value as number;

    if (partnerId && selectedPartner !== partnerId) {
      setSelectedPartner(partnerId);

      setErrors((prevErrors) => {
        const { ContractNumber, ContractDate, ...remainingErrors } = prevErrors;
        return remainingErrors;
      });

      getPartnerData(partnerId, state?.from).then((response) => {
        const { contractNumber, contractDate, termOfDue } = response.data.data;

        const invoiceDate = formData.invoiceDate
          ? moment(formData.invoiceDate)
          : moment();
        const newDueDate =
          termOfDue > 0
            ? invoiceDate.add(termOfDue, 'days').format('YYYY-MM-DD')
            : formData.dueDate;

        setFormData((prev) => ({
          ...prev,
          contractNumber,
          contractDate: contractDate?.slice(0, 10),
          termOfDue,
          dueDate: newDueDate,
        }));
      });
    }
  }, [
    formData.partnerId?.value,
    selectedPartner,
    state?.from,
    formData.invoiceDate,
    formData.dueDate,
  ]);

  const handleOpenJobNumber = () => {
    setOpenJobNumber(true);
    refetchJobNumbers();
  };

  const handleCloseJobNumber = () => {
    setOpenJobNumber(false);
    refetchJobNumbers();
  };

  const mapFieldName = (name: string) => {
    const fieldMapping: { [key: string]: string } = {
      language: 'Language',
      documentTypeId: 'DocumentTypeId',
      managerId: 'ManagerId',
      partnerId: 'PartnerId',
      invoiceJobId: 'InvoiceJobId',
      currencyId: 'CurrencyId',
      dueDate: 'DueDate',
      invoiceDate: 'InvoiceDate',
    };
    return fieldMapping[name] || name;
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === 'invoiceNumber' && errors.InvoiceNumber) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        InvoiceNumber: undefined,
      }));
    }
  };

  const handleSelectChange = (name: string, option: any) => {
    const mappedName = mapFieldName(name);
    setFormData((prevData) => ({
      ...prevData,
      [name]: option,
    }));
    if (errors[mappedName]) {
      setErrors((prevErrors) => {
        const { [mappedName]: _, ...remainingErrors } = prevErrors;
        return remainingErrors;
      });
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const payload = {
      ...formData,
      invoiceDate: formData.invoiceDate
        ? new Date(formData.invoiceDate).toISOString()
        : null,
      dueDate: formData.dueDate
        ? new Date(formData.dueDate).toISOString()
        : null,
      contractDate: formData.contractDate
        ? new Date(formData.contractDate).toISOString()
        : null,
      managerId: formData.managerId?.value || null,
      currencyId: formData.currencyId?.value || null,
      documentTypeId: formData.documentTypeId?.value || null,
      partnerId: formData.partnerId?.value || null,
      invoiceJobId: formData.invoiceJobId?.value || null,
      language: formData.language?.value || null,
    };
    const method = state?.record ? updateCostDraft : createCost;

    method(payload)
      .then((r) => {
        queryClient.invalidateQueries({
          queryKey: ['costDrafts'],
          refetchType: 'all',
        });
        queryClient.invalidateQueries({
          queryKey: [INVOICE_JOBS],
          refetchType: 'all',
        });
        queryClient.invalidateQueries({
          queryKey: [JOB_NUMBERS],
          refetchType: 'all',
        });
        const invoiceId = state?.record?.invoiceId || r.data.data;
        navigate(`/cost/line/${invoiceId}`, {
          state: {
            invoiceId: invoiceId,
            language: payload.language,
            currencyId: payload.currencyId,
          },
        });
      })
      .catch((error: NewCostCustomError) => {
        const extractedErrors = error.response?.data?.errors || {};
        error.response?.data?.Message &&
          message.error(error.response?.data?.Message);
        setErrors({
          DocumentTypeId: extractedErrors.DocumentTypeId?.[0],
          ContractNumber: extractedErrors.ContractNumber?.[0],
          ContractDate: extractedErrors.ContractDate?.[0],
          CurrencyId: extractedErrors.CurrencyId?.[0],
          ExchangeRate: extractedErrors.ExchangeRate?.[0],
          DueDate: extractedErrors.DueDate?.[0],
          InvoiceDate: extractedErrors.InvoiceDate?.[0],
          InvoiceJobId: extractedErrors.InvoiceJobId?.[0],
          InvoiceNumber: extractedErrors.InvoiceNumber?.[0],
          Language: extractedErrors.Language?.[0],
          ManagerId: extractedErrors.ManagerId?.[0],
          PartnerId: extractedErrors.PartnerId?.[0],
        });
      });
  };

  const handlePartnerAdded = async (newPartnerId: number) => {
    await refetch();

    if (partners && partners.length > 0) {
      const newPartner = partners.find((p) => p.value === newPartnerId);
      if (newPartner) {
        setLastAddedPartner(newPartner);
      }
    }
  };

  useEffect(() => {
    if (
      lastAddedPartner &&
      formattedOptions.partners.includes(lastAddedPartner)
    ) {
      setFormData((prevData) => ({
        ...prevData,
        partnerId: lastAddedPartner,
      }));
      setLastAddedPartner(null);
    }
  }, [formattedOptions.partners, lastAddedPartner]);

  useEffect(() => {
    if (accountInfoQuery.data?.timeZoneId && !isEditMode) {
      const updatedDate = moment()
        .tz(accountInfoQuery.data.timeZoneId)
        .format('YYYY-MM-DD');
      setFormData((prev) => ({
        ...prev,
        invoiceDate: updatedDate,
      }));
    }
  }, [accountInfoQuery.data?.timeZoneId, isEditMode]);

  const handleInvoiceDateChange = (
    date: moment.Moment | null,
    dateString: string
  ) => {
    if (date && accountInfoQuery.data?.timeZoneId) {
      const adjustedDate = date
        .tz(accountInfoQuery.data.timeZoneId)
        .startOf('day')
        .format('YYYY-MM-DD');
      setFormData((prev) => ({
        ...prev,
        invoiceDate: adjustedDate,
      }));
    }
  };

  const handleCurrencyChange = (option) => {
    const selectedCurrencyId = option?.value;

    setFormData((prevData) => ({
      ...prevData,
      currencyId: option,
      exchangeRate:
        selectedCurrencyId === accountInfoQuery.data?.defaultCurrencyId ? 1 : 0,
    }));

    setExchangeRateReadonly(
      selectedCurrencyId === accountInfoQuery.data?.defaultCurrencyId
    );

    if (errors.CurrencyId) {
      setErrors((prevErrors) => {
        const { CurrencyId, ...remainingErrors } = prevErrors;
        return remainingErrors;
      });
    }
  };

  const handleExchangeRateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;

    if (/^\d*\.?\d*$/.test(value) || value === '') {
      setFormData((prevData) => ({
        ...prevData,
        exchangeRate: value,
      }));
    }

    setErrors((prevErrors) => {
      const { ExchangeRate, ...remainingErrors } = prevErrors;
      return remainingErrors;
    });
  };

  const deleteInvoice = () => {
    deleteDraftCost(existingCostData?.id, existingCostData?.invoiceNumber)
      .then(() => {
        message.success(`${translation.messages.successDeleted}`);
        queryClient.invalidateQueries({ queryKey: [INVOICE_DRAFTS] });
        navigate('/invoices/drafts');
      })
      .catch((error) => {
        toastError(error);
      });
  };

  const handleCancelClick = () => {
    !!existingCostData?.id ? deleteInvoice() : navigate('/invoices/create');
  };

  return (
    <>
      {!initialValues.invoiceNumber && (
        <>
          <div className="flex flex-col gap-[32px] p-[16px] md:w-[80%] md:p-[32px] xl:w-[50%]">
            <div className="flex flex-col gap-[12px]">
              <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                {translation.InvoiceDetails.InvoiceNumber} <span>*</span>
              </label>
              <Input
                type="text"
                maxLength={20}
                onChange={handleInputChange}
                value={formData.invoiceNumber}
                name="invoiceNumber"
                placeholder={translation.InvoiceDetails.EnterInvoiceNumber}
                style={{
                  borderColor: errors.InvoiceNumber ? 'red' : '',
                }}
              />
              {errors.InvoiceNumber && (
                <div className="text-[12px] text-red-600">
                  {errors.InvoiceNumber}
                </div>
              )}
            </div>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[20px] md:gap-[12px]">
              <div className="flex flex-col gap-[12px]">
                <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                  {translation.invoicesAdd.InvoiceDate || ''}
                </label>
                <ConfigProvider locale={locale}>
                  <Space direction="vertical">
                    <DatePicker
                      placeholder={translation.reports.SelectDate}
                      style={{
                        width: '100%',
                        height: '50px',
                        borderRadius: '5px',
                        borderColor: errors.InvoiceDate ? 'red' : '',
                      }}
                      suffixIcon={
                        <div
                          className="bla"
                          style={{
                            color: errors.InvoiceDate ? 'red' : '#d9d9d9',
                          }}
                        >
                          <i className="fa-solid fa-chevron-down"></i>
                        </div>
                      }
                      onChange={handleInvoiceDateChange}
                      value={
                        formData.invoiceDate
                          ? moment(formData.invoiceDate).tz(
                              accountInfoQuery.data?.timeZoneId,
                              true
                            )
                          : null
                      }
                      format="YYYY-MM-DD"
                    />
                    {errors.InvoiceDate && (
                      <div className="text-[12px] text-red-600">
                        {errors.InvoiceDate}
                      </div>
                    )}
                  </Space>
                </ConfigProvider>
              </div>
              <div className="flex flex-col gap-[12px]">
                <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                  {translation.invoicesAdd.DueDate} <span>* </span>
                </label>
                <ConfigProvider locale={locale}>
                  <DatePicker
                    placeholder={translation.reports.SelectDate}
                    style={{
                      width: '100%',
                      height: '50px',
                      borderRadius: '5px',
                      borderColor: errors.DueDate ? 'red' : '',
                    }}
                    suffixIcon={
                      <div
                        className="bla"
                        style={{ color: errors.DueDate ? 'red' : '#d9d9d9' }}
                      >
                        <i className="fa-solid fa-chevron-down"></i>
                      </div>
                    }
                    onChange={(date, option) =>
                      handleSelectChange('dueDate', option)
                    }
                    value={formData?.dueDate ? moment(formData?.dueDate) : null}
                    format="YYYY-MM-DD"
                  />
                  {errors.DueDate && (
                    <div className="text-[12px] text-red-600">
                      {errors.DueDate}
                    </div>
                  )}
                </ConfigProvider>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="flex flex-col gap-[32px]">
              <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[20px] md:gap-[12px]">
                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                    {translation.invoicesAdd.InvoiceLang}
                  </label>
                  <Select
                    options={formattedOptions.languages}
                    classNamePrefix="react-select"
                    onChange={(option: any) =>
                      handleSelectChange('language', option)
                    }
                    name="language"
                    value={formData.language}
                  />
                </div>

                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                    {translation.invoicesAdd.DocumentType} <span>* </span>
                  </label>
                  <Select
                    onChange={(option: any) =>
                      handleSelectChange('documentTypeId', option)
                    }
                    options={docTypes}
                    classNamePrefix="react-select"
                    value={formData.documentTypeId}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: errors.DocumentTypeId
                          ? 'red'
                          : baseStyles.borderColor,
                      }),
                      menu: (baseStyles) => ({
                        ...baseStyles,
                        zIndex: 100,
                      }),
                    }}
                  />
                  {errors.DocumentTypeId && (
                    <div className="text-[12px] text-red-600">
                      {errors.DocumentTypeId}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-[12px]">
                <div className="flex items-center justify-between">
                  <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                    {translation.invoicesAdd.CustomerName} <span>* </span>
                  </label>
                  <span
                    role="button"
                    className="add-btn text-sm opacity-50"
                    onClick={handleOpenCustomerName}
                  >
                    <i className="fa-regular fa-square-plus pr-2"></i>
                    {translation.invoicesAdd.AddButton}
                  </span>
                </div>
                <Select
                  onChange={(option: any) =>
                    handleSelectChange('partnerId', option)
                  }
                  options={formattedOptions.partners}
                  value={formData.partnerId}
                  classNamePrefix="react-select"
                  filterOption={(option, inputValue) => {
                    if (!inputValue) return true;

                    const charToGroupMap = {};
                    characterGroups.forEach((group, index) => {
                      group.forEach((char) => {
                        charToGroupMap[char] = index;
                      });
                    });

                    const input = inputValue.toLowerCase();
                    const optionText = option.label.toLowerCase();

                    if (optionText.includes(input)) {
                      return true;
                    }
                    let searchIdx = 0;
                    let textIdx = 0;

                    while (
                      searchIdx < input.length &&
                      textIdx < optionText.length
                    ) {
                      const searchChar = input[searchIdx];
                      const textChar = optionText[textIdx];

                      if (searchChar === textChar) {
                        searchIdx++;
                        textIdx++;
                        continue;
                      }
                      const searchGroupIdx = charToGroupMap[searchChar];
                      const textGroupIdx = charToGroupMap[textChar];

                      if (
                        searchGroupIdx !== undefined &&
                        textGroupIdx !== undefined &&
                        searchGroupIdx === textGroupIdx
                      ) {
                        searchIdx++;
                        textIdx++;
                        continue;
                      }
                      textIdx++;
                    }

                    return searchIdx === input.length;
                  }}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: errors.PartnerId
                        ? 'red'
                        : baseStyles.borderColor,
                    }),
                    menu: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 100,
                    }),
                  }}
                />
                {errors.PartnerId && (
                  <div className="text-[12px] text-red-600">
                    {errors.PartnerId}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[20px] md:gap-[12px]">
                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                    {translation.invoicesAdd.ContractNumber}
                  </label>
                  <Input
                    type="text"
                    value={formData.contractNumber}
                    onChange={handleInputChange}
                    name="contractNumber"
                    maxLength={9}
                    placeholder="987652314"
                    readOnly
                    style={{
                      borderColor: errors.ContractNumber ? 'red' : '',
                    }}
                  />
                  {errors.ContractNumber && (
                    <div className="text-[12px] text-red-600">
                      {errors.ContractNumber}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                    {translation.invoicesAdd.ContractDate}
                  </label>
                  <ConfigProvider locale={locale}>
                    <DatePicker
                      placeholder={translation.reports.SelectDate}
                      style={{
                        width: '100%',
                        height: '50px',
                        borderRadius: '5px',
                        borderColor: errors.ContractDate ? 'red' : '',
                      }}
                      disabled={true}
                      onChange={(date, dateString) =>
                        setFormData((prev) => ({
                          ...prev,
                          contractDate: dateString,
                        }))
                      }
                      suffixIcon={
                        <div
                          className="bla"
                          style={{
                            color: errors.InvoiceDate ? 'red' : '#d9d9d9',
                          }}
                        >
                          <i className="fa-solid fa-chevron-down"></i>
                        </div>
                      }
                      value={
                        formData.contractDate
                          ? moment(formData.contractDate)
                          : null
                      }
                      format="YYYY-MM-DD"
                    />
                  </ConfigProvider>

                  {errors.ContractDate && (
                    <div className="text-[12px] text-red-600">
                      {errors.ContractDate}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-[12px]">
                <div className="flex items-center justify-between">
                  <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                    {translation.invoicesAdd.Manager}
                  </label>
                  <span
                    role="button"
                    className="add-btn pe-auto text-sm opacity-50"
                    onClick={handleOpenManager}
                  >
                    <i className="fa-regular fa-square-plus pr-2"></i>
                    {translation.invoicesAdd.AddButton}
                  </span>
                </div>
                <Select
                  options={formattedOptions.managers}
                  onChange={(option: any) =>
                    handleSelectChange('managerId', option)
                  }
                  value={formData.managerId}
                  classNamePrefix="react-select"
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: errors.ManagerId
                        ? 'red'
                        : baseStyles.borderColor,
                    }),
                    menu: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 100,
                    }),
                  }}
                />
                {errors.ManagerId && (
                  <div className="text-[12px] text-red-600">
                    {errors.ManagerId}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-[12px]">
                <div className="flex items-center justify-between">
                  <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                    {translation.invoicesAdd.JobNumber}
                  </label>
                  <span
                    role="button"
                    className="add-btn text-sm opacity-50"
                    onClick={handleOpenJobNumber}
                  >
                    <i className="fa-regular fa-square-plus mr-2"></i>
                    {translation.invoicesAdd.AddButton}
                  </span>
                </div>
                <Select
                  onChange={(option: any) =>
                    handleSelectChange('invoiceJobId', option)
                  }
                  classNamePrefix="react-select"
                  options={formattedOptions.jobNumbers}
                  value={formData.invoiceJobId}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: errors.InvoiceJobId
                        ? 'red'
                        : baseStyles.borderColor,
                    }),
                    menu: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 100,
                    }),
                  }}
                />
                {errors.InvoiceJobId && (
                  <div className="text-[12px] text-red-600">
                    {errors.InvoiceJobId}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[20px] md:gap-[12px]">
                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                    {translation.invoicesAdd.Currency} <span>* </span>
                  </label>
                  <Select
                    onChange={handleCurrencyChange}
                    options={formattedOptions.currencies}
                    classNamePrefix="react-select"
                    value={formData.currencyId}
                    menuPlacement="top"
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: errors.CurrencyId
                          ? 'red'
                          : baseStyles.borderColor,
                      }),
                      menu: (baseStyles) => ({
                        ...baseStyles,
                        zIndex: 100,
                      }),
                    }}
                  />
                  {errors.CurrencyId && (
                    <div className="text-[12px] text-red-600">
                      {errors.CurrencyId}
                    </div>
                  )}
                </div>
                {!exchangeRateReadonly && (
                  <div className="flex flex-col gap-[12px]">
                    <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                      {translation.invoicesAdd.ExchRate}
                    </label>
                    <Input
                      className="d-block"
                      type="text"
                      disabled={exchangeRateReadonly}
                      maxLength={9}
                      value={formData.exchangeRate}
                      onChange={handleExchangeRateChange}
                      onFocus={(e) => (e.target.value = '')}
                      onBlur={(e) => {
                        if (!e.target.value) {
                          setFormData({ ...formData, exchangeRate: 0 });
                        }
                      }}
                      style={{
                        borderColor: errors.ExchangeRate ? 'red' : '',
                      }}
                    />
                    {errors.ExchangeRate && (
                      <div className="text-[12px] text-red-600">
                        {errors.ExchangeRate}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="w-full rounded-[6px] bg-[#466afb] px-[24px] py-[12px] text-[14px] font-normal leading-[16px] text-white md:text-[16px]"
              >
                {state?.record
                  ? translation.invoicesAdd.UpdateItemButton
                  : translation.invoicesAdd.AddItemButton}
              </button>
            </form>
          </div>
        </>
      )}

      <AddNewManagerForm open={openManager} handleClose={handleCloseManager} />
      <AddNewPartnerForm
        open={openCustomerName}
        transactionType={state?.from}
        handleClose={handleCloseCustomerName}
        onSuccess={(newPartnerId) => handlePartnerAdded(newPartnerId)}
        isCreditor={true}
      />
      <AddNewJobNumberForm
        open={openJobNumber}
        handleClose={handleCloseJobNumber}
      />
    </>
  );
};

export default NewCost;

const Input = styled.input`
  border-radius: 6px;
  padding: 11.9px 15px;
  border: 1px solid rgba(41, 45, 50, 0.25);
  :focus {
    outline: none !important;
    border: 2px solid #466afb;
  }
  &[type='date'] {
    padding: 10.9px 15px;
  }
`;

const Select = styled(ReactSelect)`
  & .react-select__value-container {
    padding: 8.7px 16px;
  }
`;
